import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { ApolloError, MutationFunction, MutationResult } from '@apollo/client';
import { Mutation } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';
import { ReadonlyProperty, UpdatePerson } from '../../../../../../Apollo';
import ButtonLoading from 'Theme/components/ButtonLoading';
import { ErrorHandler } from 'Theme/components/ErrorHandler';
import TestIds from 'Tests/TestIds';

const styles = (theme: Theme) =>
  createStyles({
    textField: {
      marginBottom: theme.spacing(3),

      '&:last-child': {
        marginBottom: 0
      }
    }
  });

interface Props {
  person: IPersonWithAccount;
  onClose: () => void;
  readonlyProperties: ReadonlyProperty[];
}

interface State {
  phoneNumber: string;
  mobileNumber: string;
}

class EditPhoneDialog extends React.Component<
  Props & WithStyles<typeof styles>,
  State
> {
  public readonly state: State = {
    phoneNumber: this.props.person.phoneNumber || '',
    mobileNumber: this.props.person.mobileNumber || ''
  };

  public render() {
    const { person, readonlyProperties, onClose, classes } = this.props;
    const { phoneNumber, mobileNumber } = this.state;

    const getInputProps = (name: keyof State) => ({
      classes: { root: classes.textField },
      name,
      label: <FormattedMessage id={`page.userProfile.editPhone.${name}`} />,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState<any>({
          [name]: event.target.value
        });
      },
      fullWidth: true,
      value: this.state[name]
    });

    return (
      <ErrorHandler.Consumer>
        {errorHandler => (
          <Mutation
            mutation={UpdatePerson({
              phoneNumber,
              mobileNumber
            })}
            variables={{
              personIdentifier: person.immutableId
            }}
            onError={(e: ApolloError) => errorHandler.onError(e)}
            onCompleted={this.onCompleted}
            refetchQueries={['GetPersonWithAccount']}
          >
            {(updatePerson: MutationFunction, { loading }: MutationResult) => (
              <Dialog
                open={true}
                onClose={onClose}
                maxWidth="xs"
                fullWidth={true}
              >
                <form
                  onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();

                    if (loading) {
                      return;
                    }

                    updatePerson();
                  }}
                >
                  <DialogTitle>
                    <FormattedMessage id="page.userProfile.editPhone.editPhone" />
                  </DialogTitle>
                  <DialogContent>
                    {!readonlyProperties.includes(
                      ReadonlyProperty.PhoneNumber
                    ) && (
                      <TextField
                        {...getInputProps('phoneNumber')}
                        variant='outlined'
                        autoFocus={true}
                      />
                    )}

                    {!readonlyProperties.includes(
                      ReadonlyProperty.MobileNumber
                    ) && <TextField {...getInputProps('mobileNumber')} variant='outlined'/>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={onClose} data-testid={TestIds.common.snackbar.action.cancel} color='inherit'>
                      <FormattedMessage id="common.action.cancel" />
                    </Button>
                    <ButtonLoading
                      data-testid={TestIds.pages.profile.savePhoneButton}
                      type="submit"
                      color="primary"
                      loading={loading}
                    >
                      <FormattedMessage id="common.action.validate" />
                    </ButtonLoading>
                  </DialogActions>
                </form>
              </Dialog>
            )}
          </Mutation>
        )}
      </ErrorHandler.Consumer>
    );
  }

  private onCompleted = () => {
    const { onClose } = this.props;

    enqueueSnackbar(<FormattedMessage id="page.userProfile.updatedProfile" />, {
      variant: 'success'
    });

    onClose();
  };
}

export default withStyles(styles)(EditPhoneDialog);
